// IndexedDB 文本存储工具（按登录用户隔离库名，避免换号后文案缓存串号）
import { getIpeUserLocalOwnerId } from './ipeUserLocalState';
import { ownerScopedIndexedDbName } from './ipeUserIndexedDb';

const DB_NAME = 'funding_text_storage';
const DB_VERSION = 1;
const STORE_NAME = 'texts';

let dbInstance = null;
/** @type {string|null} */
let dbInstanceOwner = null;
/** @type {string|null} */
let dbInstanceName = null;

function closeDbInstance() {
  if (dbInstance) {
    try {
      dbInstance.close();
    } catch {
      /* ignore */
    }
  }
  dbInstance = null;
  dbInstanceOwner = null;
  dbInstanceName = null;
}

/** 换号 / 退出时关闭连接 */
export function resetTextStorageForOwnerChange() {
  closeDbInstance();
}

// 打开当前用户作用域数据库
const openDB = () => {
  return new Promise((resolve, reject) => {
    const owner = getIpeUserLocalOwnerId();
    const name = ownerScopedIndexedDbName(DB_NAME);
    if (!owner || !name) {
      reject(new Error('未绑定登录用户，无法访问本地文本存储'));
      return;
    }

    if (dbInstance && dbInstanceOwner === owner && dbInstanceName === name) {
      resolve(dbInstance);
      return;
    }

    closeDbInstance();

    const request = indexedDB.open(name, DB_VERSION);

    request.onerror = () => {
      reject(new Error('打开数据库失败'));
    };

    request.onsuccess = () => {
      dbInstance = request.result;
      dbInstanceOwner = owner;
      dbInstanceName = name;
      resolve(dbInstance);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
  });
};

// 保存文本内容到 IndexedDB
export const saveTextToIndexedDB = async (textId, textContent, fileName) => {
  try {
    if (!textId || !textContent) {
      throw new Error('文本 ID 或内容不能为空');
    }
    
    const db = await openDB();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.put({ 
        id: textId, 
        textContent: textContent,
        fileName: fileName || 'text.txt',
        updatedAt: new Date().toISOString(),
      });
      
      request.onsuccess = () => {
        resolve(true);
      };
      
      request.onerror = (e) => {
        console.error(`保存文本 ${textId} 到 IndexedDB 失败:`, e);
        reject(new Error('保存文本到 IndexedDB 失败'));
      };
    });
  } catch (error) {
    console.error(`保存文本 ${textId} 失败:`, error);
    throw error;
  }
};

// 从 IndexedDB 读取文本内容
export const getTextFromIndexedDB = async (textId) => {
  try {
    if (!textId) {
      console.warn('文本 ID 为空');
      return null;
    }
    if (!getIpeUserLocalOwnerId()) {
      return null;
    }
    
    const db = await openDB();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(textId);
      
      request.onsuccess = () => {
        const result = request.result;
        if (result) {
          resolve({
            textContent: result.textContent,
            fileName: result.fileName || 'text.txt',
          });
        } else {
          console.warn(`文本 ${textId} 在 IndexedDB 中不存在`);
          resolve(null);
        }
      };
      
      request.onerror = (e) => {
        console.error(`从 IndexedDB 读取文本 ${textId} 失败:`, e);
        reject(new Error('从 IndexedDB 读取文本失败'));
      };
    });
  } catch (error) {
    console.error(`读取文本 ${textId} 失败:`, error);
    return null;
  }
};

// 从 IndexedDB 删除文本
export const deleteTextFromIndexedDB = async (textId) => {
  try {
    const db = await openDB();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete(textId);
      
      request.onsuccess = () => {
        resolve(true);
      };
      
      request.onerror = () => {
        reject(new Error('从 IndexedDB 删除文本失败'));
      };
    });
  } catch (error) {
    console.error('删除文本失败:', error);
    throw error;
  }
};

// 保存演示文档到 IndexedDB
export const saveDemoFileToIndexedDB = async (textId, fileData, fileName) => {
  try {
    if (!textId || !fileData) {
      throw new Error('文本 ID 或文件数据不能为空');
    }
    
    const db = await openDB();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      
      // 获取现有数据
      const getRequest = store.get(textId);
      getRequest.onsuccess = () => {
        const existing = getRequest.result || { id: textId };
        const updateData = {
          ...existing,
          demoFile: fileData, // base64 数据
          demoFileName: fileName || 'demo.pdf',
          demoFileUpdatedAt: new Date().toISOString(),
        };
        
        const putRequest = store.put(updateData);
        putRequest.onsuccess = () => {
          resolve(true);
        };
        putRequest.onerror = (e) => {
          console.error(`保存演示文档 ${textId} 到 IndexedDB 失败:`, e);
          reject(new Error('保存演示文档到 IndexedDB 失败'));
        };
      };
      getRequest.onerror = (e) => {
        console.error(`读取数据 ${textId} 失败:`, e);
        reject(new Error('读取数据失败'));
      };
    });
  } catch (error) {
    console.error(`保存演示文档 ${textId} 失败:`, error);
    throw error;
  }
};

// 从 IndexedDB 读取演示文档
export const getDemoFileFromIndexedDB = async (textId) => {
  try {
    if (!textId) {
      console.warn('文本 ID 为空');
      return null;
    }
    if (!getIpeUserLocalOwnerId()) {
      return null;
    }
    
    const db = await openDB();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(textId);
      
      request.onsuccess = () => {
        const result = request.result;
        if (result && result.demoFile) {
          resolve({
            demoFile: result.demoFile,
            demoFileName: result.demoFileName || 'demo.pdf',
          });
        } else {
          resolve(null);
        }
      };
      
      request.onerror = (e) => {
        console.error(`从 IndexedDB 读取演示文档 ${textId} 失败:`, e);
        reject(new Error('从 IndexedDB 读取演示文档失败'));
      };
    });
  } catch (error) {
    console.error(`读取演示文档 ${textId} 失败:`, error);
    return null;
  }
};

// 批量获取文本内容
export const getTextsFromIndexedDB = async (textIds) => {
  try {
    if (!textIds || textIds.length === 0) {
      return [];
    }
    
    const db = await openDB();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const results = [];
      let completed = 0;
      
      textIds.forEach((textId) => {
        const request = store.get(textId);
        request.onsuccess = () => {
          const result = request.result;
          if (result) {
            results.push({
              id: textId,
              textContent: result.textContent,
              fileName: result.fileName || 'text.txt',
            });
          }
          completed++;
          if (completed === textIds.length) {
            resolve(results);
          }
        };
        request.onerror = () => {
          completed++;
          if (completed === textIds.length) {
            resolve(results);
          }
        };
      });
    });
  } catch (error) {
    console.error('批量读取文本失败:', error);
    return [];
  }
};
